import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';

import KlingSvg from '~/svg/Kling.svg';

/**
 * Official Kling 4.0 demonstrations from Kling AI's release note
 * (kling.ai/dev/model-release/kling-4), re-hosted on our CDN: downloaded,
 * re-encoded (≤1280px wide, 1600px for the 21:9 shot, H.264 CRF 27,
 * faststart, AAC 96k) and uploaded with poster frames to the R2 `images`
 * bucket under `landing/kling-4-0/`. Every clip keeps its original sound.
 *
 * Adding one: re-encode and upload the clip and a poster the same way, curl
 * the CDN URL until it answers 200, and only then reference it here.
 * Cloudflare caches a 404 for a year, so a key that was ever requested before
 * its upload must never be reused — pick a new name instead.
 */
const CDN = 'https://media.easy-peasy.ai/landing/kling-4-0';
const clip = (name: string) => `${CDN}/${name}.mp4`;
const poster = (name: string) => `${CDN}/${name}-poster.jpg`;

/**
 * Every call to action opens plain Kling 4.0. The Reference and Keyframes
 * entries need images attached first — the generator switches back to its
 * default model when one of them is selected with nothing attached — and
 * adding media to Kling 4.0 there moves the user to them anyway.
 */
const GENERATOR_URL = '/ai-videos?model=kling-4.0';
const EDIT_URL = '/ai-videos?tab=edit-video';
const EXTEND_URL = '/ai-videos?tab=extend-video';
const OFFICIAL_URL = 'https://kling.ai/dev/model-release/kling-4';

type Resolution = '720p' | '1080p';

export interface Kling40Pricing {
  /** Credits per second of output, by resolution. */
  generation: Record<Resolution, number>;
  /** Credits per added second on the Extend tool. */
  extend: Record<Resolution, number>;
  /** Credits per second of the source clip on the Edit tool. */
  edit: Record<Resolution, number>;
  /** The generator's duration ladder, in seconds. */
  durations: number[];
  /** The Extend tool's ladder for the new segment, in seconds. */
  extendDurations: number[];
  /** Source-clip window the Edit tool accepts, in seconds. */
  editRange: [number, number];
}

export interface Kling40LandingProps {
  pricing: Kling40Pricing;
}

type T = (key: string) => string;

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className='mb-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-teal-200'>
    <span className='h-px w-6 bg-gradient-to-r from-teal-300 to-lime-300 rtl:bg-gradient-to-l' />
    {children}
  </span>
);

const SectionHeading = ({
  eyebrow,
  title,
  lead,
  center = true,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  center?: boolean;
}) => (
  <div className={clsxm('max-w-3xl', center && 'mx-auto text-center')}>
    <Eyebrow>{eyebrow}</Eyebrow>
    {/* Japanese breaks between phrases (never inside a katakana word), Korean
        between words. */}
    <h2 className='text-balance text-3xl font-semibold tracking-[-0.03em] text-white [line-break:strict] [word-break:auto-phrase] sm:text-5xl [&:lang(ko)]:[word-break:keep-all]'>
      {title}
    </h2>
    {lead && (
      <p
        className={clsxm(
          'mt-6 text-pretty text-lg leading-relaxed text-slate-400',
          center && 'mx-auto max-w-2xl',
        )}
      >
        {lead}
      </p>
    )}
  </div>
);

const Check = () => (
  <svg
    viewBox='0 0 20 20'
    fill='currentColor'
    aria-hidden='true'
    className='mt-0.5 h-4 w-4 flex-shrink-0 text-teal-300'
  >
    <path
      fillRule='evenodd'
      d='M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z'
      clipRule='evenodd'
    />
  </svg>
);

/** True once the element has scrolled near the viewport. */
function useNearViewport<T extends Element>(rootMargin = '200px') {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near, rootMargin]);
  return [ref, near] as const;
}

// ---------------------------------------------------------------------------
// Framing: one 21:9 shot with guides for the other three formats
// ---------------------------------------------------------------------------

const FRAMES = [
  { ratio: '21:9', value: 21 / 9, use: 'Cinema' },
  { ratio: '16:9', value: 16 / 9, use: 'YouTube & web' },
  { ratio: '9:16', value: 9 / 16, use: 'Reels, TikTok & Shorts' },
  { ratio: '1:1', value: 1, use: 'Feeds' },
] as const;
const ULTRAWIDE = 21 / 9;

function FramingSection({ t }: { t: T }) {
  const [frame, setFrame] = useState<(typeof FRAMES)[number]>(FRAMES[0]);
  const [ref, near] = useNearViewport<HTMLDivElement>();
  // Guide size relative to the 21:9 stage: narrower formats keep the full
  // height and give up width.
  const width =
    frame.value >= ULTRAWIDE ? 100 : (frame.value / ULTRAWIDE) * 100;

  return (
    <section
      id='framing'
      className='border-t border-white/[0.06] py-24 sm:py-32'
    >
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end'>
          <SectionHeading
            center={false}
            eyebrow={t('Four Formats')}
            title={t('Frame It Like Cinema')}
            lead={t(
              'Kling 4.0 renders 21:9 ultrawide alongside 16:9, 9:16 and 1:1. Pick the format before you generate and compose the shot for the screen it is made for.',
            )}
          />
          <div
            className='flex flex-wrap gap-2 lg:justify-end'
            role='group'
            aria-label={t('Aspect ratio')}
          >
            {FRAMES.map((option) => (
              <button
                key={option.ratio}
                type='button'
                aria-pressed={frame.ratio === option.ratio}
                onClick={() => setFrame(option)}
                className={clsxm(
                  'rounded-full border px-4 py-2 text-start transition-colors',
                  frame.ratio === option.ratio
                    ? 'border-teal-300/60 bg-teal-300/10 text-white'
                    : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-white',
                )}
              >
                <span className='block text-sm font-semibold'>
                  {option.ratio}
                </span>
                <span className='block text-[11px] text-slate-500'>
                  {t(option.use)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          ref={ref}
          className='relative mt-12 overflow-hidden rounded-[28px] border border-white/[0.08] bg-black shadow-[0_40px_120px_-40px_rgba(45,212,191,0.35)]'
          style={{ aspectRatio: '21 / 9' }}
        >
          {near ? (
            <video
              src={clip('ultrawide-drama')}
              poster={poster('ultrawide-drama')}
              autoPlay
              muted
              loop
              playsInline
              preload='metadata'
              className='absolute inset-0 h-full w-full object-cover'
            />
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={poster('ultrawide-drama')}
              alt=''
              loading='lazy'
              className='absolute inset-0 h-full w-full object-cover'
            />
          )}
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 rounded-[6px] border border-white/60 transition-[width] duration-500 ease-out'
            style={{
              width: `${width}%`,
              boxShadow: '0 0 0 9999px rgba(2, 6, 6, 0.62)',
            }}
          >
            <span className='absolute start-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur'>
              {frame.ratio}
            </span>
          </div>
        </div>
        <p className='mt-4 text-sm text-slate-500'>
          {t(
            'An official Kling 4.0 example shot in 21:9. The guide previews how each format frames a scene — every format is generated natively, never cropped.',
          )}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The reel: one large player, a chapter list beside it
// ---------------------------------------------------------------------------

const REEL = [
  {
    clip: 'reel-boxing-arena',
    title: 'Large-Scale Action',
    description:
      'Full-body fight choreography that holds steady through fast moves, hard impacts and a crowd on its feet.',
  },
  {
    clip: 'camera-desert-drift',
    title: 'Tracking Shots',
    description:
      'A low tracking shot follows a car drifting across the dunes while the sand and spray move with it.',
  },
  {
    clip: 'reel-dialogue',
    title: 'Dialogue & Lip-Sync',
    description:
      'Two actors argue in one take — voices, timing and expressions stay in sync.',
  },
  {
    clip: 'reel-studio-singer',
    title: 'Music & Stereo Sound',
    description:
      'A vocalist in the booth: the singing, the breaths and the room tone are generated with the performance.',
  },
  {
    clip: 'reel-dragon-siege',
    title: 'Multi-Shot Story',
    description:
      'A 28-second dragon attack told across several shots in a single generation.',
  },
  {
    clip: 'reel-vlog',
    title: 'Natural Presenters',
    description:
      'A creator talks to camera with relaxed gestures and believable expressions.',
  },
  {
    clip: 'reel-kinetic-type',
    title: 'Text in Motion',
    description:
      'Bold headlines and graphic layouts rendered and animated right in the frame.',
  },
] as const;

function Reel({ t }: { t: T }) {
  const [active, setActive] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stageRef, near] = useNearViewport<HTMLDivElement>('0px');
  const item = REEL[active];

  // A new chapter starts from the top, playing — muted unless the visitor
  // asked for sound, which the click on the sound button allows.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !near) return;
    video.muted = !soundOn;
    video.currentTime = 0;
    video.play().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, near]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    const video = videoRef.current;
    if (video) {
      video.muted = !next;
      video.play().catch(() => undefined);
    }
  };

  return (
    <section id='reel' className='border-t border-white/[0.06] py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <SectionHeading
          eyebrow={t('Official Examples')}
          title={t('See What Kling 4.0 Can Do')}
          lead={t(
            'Every clip on this page comes from Kling AI’s official Kling 4.0 release. Turn the sound on — the audio is generated with the picture.',
          )}
        />
        <div className='mt-6 text-center'>
          <a
            href={OFFICIAL_URL}
            target='_blank'
            rel='noreferrer'
            className='text-sm font-medium text-teal-200 underline decoration-teal-200/30 underline-offset-4 transition-colors hover:text-white'
          >
            {t('Read the official release note')}{' '}
            <span className='inline-block rtl:-scale-x-100'>↗</span>
          </a>
        </div>

        <div className='mt-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]'>
          <div
            ref={stageRef}
            className='relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-black'
          >
            <video
              key={item.clip}
              ref={videoRef}
              src={near ? clip(item.clip) : undefined}
              poster={poster(item.clip)}
              muted
              loop
              playsInline
              preload={near ? 'auto' : 'none'}
              className='aspect-video w-full object-cover'
            />
            <div className='pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-5 pt-24 sm:p-7 sm:pt-28'>
              <p className='text-xs font-bold uppercase tracking-[0.2em] text-teal-200'>
                {String(active + 1).padStart(2, '0')} / {REEL.length}
              </p>
              <h3 className='mt-2 text-xl font-semibold text-white sm:text-2xl'>
                {t(item.title)}
              </h3>
              <p className='mt-1 max-w-xl text-sm leading-relaxed text-slate-300'>
                {t(item.description)}
              </p>
            </div>
            <button
              type='button'
              onClick={toggleSound}
              aria-pressed={soundOn}
              className='absolute end-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/55 px-3.5 py-2 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-black/75'
            >
              <span aria-hidden='true'>{soundOn ? '🔊' : '🔇'}</span>
              {soundOn ? t('Sound on') : t('Sound off')}
            </button>
          </div>

          <ol className='flex snap-x gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0'>
            {REEL.map((chapter, index) => (
              <li
                key={chapter.clip}
                className='min-w-[230px] snap-start lg:min-w-0'
              >
                <button
                  type='button'
                  onClick={() => setActive(index)}
                  aria-current={index === active}
                  className={clsxm(
                    'group flex w-full items-center gap-3 rounded-2xl border p-2.5 text-start transition-colors',
                    index === active
                      ? 'border-teal-300/50 bg-teal-300/[0.08]'
                      : 'border-white/[0.06] bg-white/[0.02] hover:border-white/20',
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={poster(chapter.clip)}
                    alt=''
                    loading='lazy'
                    className='h-12 w-20 flex-shrink-0 rounded-lg object-cover'
                  />
                  <span className='min-w-0'>
                    <span
                      className={clsxm(
                        'block truncate text-sm font-semibold',
                        index === active ? 'text-white' : 'text-slate-300',
                      )}
                    >
                      {t(chapter.title)}
                    </span>
                    <span className='block text-[11px] text-slate-500'>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Omni Reference: the input clip against the Kling 4.0 result
// ---------------------------------------------------------------------------

const REFERENCE_PAIRS = [
  {
    source: 'ref-graybox-source',
    result: 'ref-graybox-result',
    title: 'From Gray-Box to Final Cast',
    description:
      'A gray-box animation sets the choreography and the camera; reference images supply the cast and the location.',
  },
  {
    source: 'ref-swap-source',
    result: 'ref-swap-result',
    title: 'Swap the Person and the Room',
    description:
      'The take keeps its timing and its lines while a new character and a new living room replace the old ones.',
  },
  {
    source: 'ref-removal-source',
    result: 'ref-removal-result',
    title: 'Clear the Crowd',
    description:
      'The passers-by in front of the band disappear while the street performance plays on.',
  },
] as const;

function ReferenceCompare({
  pair,
  t,
}: {
  pair: (typeof REFERENCE_PAIRS)[number];
  t: T;
}) {
  const [side, setSide] = useState<'source' | 'result'>('result');
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const name = side === 'source' ? pair.source : pair.result;

  // Switching keeps the playhead so the two versions line up frame for frame.
  const switchSide = (next: 'source' | 'result') => {
    const current = videoRef.current?.currentTime ?? 0;
    setSide(next);
    requestAnimationFrame(() => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = current;
      if (playing) video.play().catch(() => undefined);
    });
  };

  useEffect(() => {
    if (playing) videoRef.current?.play().catch(() => undefined);
  }, [playing, side]);

  return (
    <article className='group'>
      <div className='relative overflow-hidden rounded-3xl border border-white/[0.08] bg-black'>
        {playing ? (
          <video
            key={name}
            ref={videoRef}
            src={clip(name)}
            poster={poster(name)}
            muted
            loop
            playsInline
            controls
            preload='auto'
            className='aspect-video w-full object-cover'
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={poster(name)}
            alt={t(pair.title)}
            loading='lazy'
            className='aspect-video w-full object-cover'
          />
        )}
        {!playing && (
          <button
            type='button'
            onClick={() => setPlaying(true)}
            aria-label={t('Play the example')}
            className='absolute inset-0 flex items-center justify-center bg-black/15 transition-colors hover:bg-black/30'
          >
            <span className='flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-lg text-black shadow-xl transition-transform group-hover:scale-105'>
              ▶
            </span>
          </button>
        )}
        <div
          className='absolute start-3 top-3 flex rounded-full border border-white/15 bg-black/60 p-1 backdrop-blur'
          role='group'
          aria-label={t('Compare')}
        >
          {(['source', 'result'] as const).map((value) => (
            <button
              key={value}
              type='button'
              aria-pressed={side === value}
              onClick={() => switchSide(value)}
              className={clsxm(
                'rounded-full px-3 py-1 text-[11px] font-semibold transition-colors',
                side === value
                  ? 'bg-teal-300 text-[#04201b]'
                  : 'text-slate-300 hover:text-white',
              )}
            >
              {value === 'source' ? t('Reference') : t('Kling 4.0')}
            </button>
          ))}
        </div>
      </div>
      <h3 className='mt-4 text-lg font-semibold text-white'>{t(pair.title)}</h3>
      <p className='mt-1.5 text-sm leading-relaxed text-slate-400'>
        {t(pair.description)}
      </p>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Styles: hover (or tap) to play
// ---------------------------------------------------------------------------

const STYLES = [
  { clip: 'style-claymation', label: 'Claymation' },
  { clip: 'style-felt', label: 'Felt Art' },
  { clip: 'style-ink', label: 'Ink Painting' },
  { clip: 'style-cyberpunk', label: '3D Cyberpunk' },
  { clip: 'style-anime', label: 'Anime Action' },
] as const;

function StyleTile({ item, t }: { item: (typeof STYLES)[number]; t: T }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const play = () => {
    videoRef.current?.play().catch(() => undefined);
    setPlaying(true);
  };
  const stop = () => {
    videoRef.current?.pause();
    setPlaying(false);
  };

  return (
    <button
      type='button'
      onMouseEnter={play}
      onMouseLeave={stop}
      onFocus={play}
      onBlur={stop}
      onClick={() => (playing ? stop() : play())}
      aria-pressed={playing}
      className='group relative block w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-black text-start'
    >
      <video
        ref={videoRef}
        src={clip(item.clip)}
        poster={poster(item.clip)}
        muted
        loop
        playsInline
        preload='none'
        className='aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] sm:aspect-[3/4]'
      />
      <span className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 pt-12'>
        <span className='block text-sm font-semibold text-white'>
          {t(item.label)}
        </span>
        <span className='mt-0.5 block text-[11px] text-slate-400'>
          {playing ? t('Playing') : t('Hover or tap to play')}
        </span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Credit calculator
// ---------------------------------------------------------------------------

type CalcMode = 'generate' | 'extend' | 'edit';

function CreditCalculator({ pricing, t }: { pricing: Kling40Pricing; t: T }) {
  const [mode, setMode] = useState<CalcMode>('generate');
  const [resolution, setResolution] = useState<Resolution>('720p');

  const ladder = useMemo(() => {
    if (mode === 'generate') return pricing.durations;
    if (mode === 'extend') {
      return [...pricing.extendDurations].sort((a, b) => a - b);
    }
    const [min, max] = pricing.editRange;
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, [mode, pricing]);
  const [step, setStep] = useState(() =>
    Math.max(0, pricing.durations.indexOf(5)),
  );
  const index = Math.min(step, ladder.length - 1);
  const seconds = ladder[index];
  const perSecond =
    mode === 'edit'
      ? pricing.edit[resolution]
      : mode === 'extend'
        ? pricing.extend[resolution]
        : pricing.generation[resolution];
  const credits = Math.ceil(seconds * perSecond);

  const changeMode = (next: CalcMode) => {
    const nextLadder =
      next === 'generate'
        ? pricing.durations
        : next === 'extend'
          ? [...pricing.extendDurations].sort((a, b) => a - b)
          : Array.from(
              { length: pricing.editRange[1] - pricing.editRange[0] + 1 },
              (_, i) => pricing.editRange[0] + i,
            );
    // Keep the same length where the new mode offers it.
    const keep = nextLadder.indexOf(seconds);
    setStep(keep >= 0 ? keep : Math.max(0, nextLadder.indexOf(5)));
    setMode(next);
  };

  const modes: { value: CalcMode; label: string; unit: string }[] = [
    {
      value: 'generate',
      label: t('Generate'),
      unit: t('Video length'),
    },
    { value: 'extend', label: t('Extend'), unit: t('Seconds added') },
    { value: 'edit', label: t('Edit'), unit: t('Length of your clip') },
  ];
  const current = modes.find((option) => option.value === mode) ?? modes[0];

  return (
    <div className='rounded-[28px] border border-white/[0.08] bg-[linear-gradient(160deg,rgba(45,212,191,0.10),rgba(255,255,255,0.02)_45%)] p-6 sm:p-8'>
      <div
        className='flex flex-wrap gap-2'
        role='group'
        aria-label={t('What are you making?')}
      >
        {modes.map((option) => (
          <button
            key={option.value}
            type='button'
            aria-pressed={mode === option.value}
            onClick={() => changeMode(option.value)}
            className={clsxm(
              'rounded-full px-4 py-2 text-sm font-semibold transition-colors',
              mode === option.value
                ? 'bg-white text-[#06110f]'
                : 'border border-white/10 text-slate-300 hover:border-white/25',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className='mt-8 flex items-end justify-between gap-6'>
        <div>
          <label
            htmlFor='kling-seconds'
            className='text-xs font-semibold uppercase tracking-[0.18em] text-slate-500'
          >
            {current.unit}
          </label>
          <div className='mt-1 text-4xl font-semibold tabular-nums text-white'>
            {seconds}
            <span className='ms-1 text-lg text-slate-500'>s</span>
          </div>
        </div>
        <div className='text-end'>
          <div className='text-xs font-semibold uppercase tracking-[0.18em] text-slate-500'>
            {t('Credits')}
          </div>
          <div className='mt-1 bg-gradient-to-r from-teal-200 to-lime-200 bg-clip-text text-5xl font-semibold tabular-nums text-transparent'>
            {credits}
          </div>
        </div>
      </div>

      <input
        id='kling-seconds'
        type='range'
        min={0}
        max={ladder.length - 1}
        step={1}
        value={index}
        onChange={(event) => setStep(Number(event.target.value))}
        aria-valuetext={`${seconds} s`}
        className='mt-6 w-full accent-teal-300'
      />
      <div className='mt-1 flex justify-between text-[11px] text-slate-500'>
        <span>{ladder[0]}s</span>
        <span>{ladder[ladder.length - 1]}s</span>
      </div>

      <div
        className='mt-6 flex gap-2'
        role='group'
        aria-label={t('Resolution')}
      >
        {(['720p', '1080p'] as const).map((value) => (
          <button
            key={value}
            type='button'
            aria-pressed={resolution === value}
            onClick={() => setResolution(value)}
            className={clsxm(
              'flex-1 rounded-xl border px-4 py-3 text-start transition-colors',
              resolution === value
                ? 'border-teal-300/50 bg-teal-300/[0.08]'
                : 'border-white/10 hover:border-white/25',
            )}
          >
            <span className='block text-sm font-semibold text-white'>
              {value}
            </span>
            <span className='block text-[11px] text-slate-500'>
              {(mode === 'edit'
                ? pricing.edit[value]
                : mode === 'extend'
                  ? pricing.extend[value]
                  : pricing.generation[value]) +
                ' ' +
                t('credits / second')}
            </span>
          </button>
        ))}
      </div>

      <p className='mt-6 text-xs leading-relaxed text-slate-500'>
        {mode === 'edit'
          ? t(
              'Edits are billed per second of the clip you upload, because the model processes the original and the new version.',
            )
          : mode === 'extend'
            ? t(
                'Only the new seconds are billed; your original clip is kept and joined to them.',
              )
            : t(
                'Attaching reference video clips adds to the price, because the reference footage is processed too. The exact total is always shown before you generate.',
              )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Kling40Landing({ pricing }: Kling40LandingProps) {
  const { t: translate } = useTranslation('kling-4-0');
  const t: T = (key) => translate(key, undefined, { default: key });
  const { locale = 'en' } = useRouter();

  const title = 'Kling 4.0 AI Video Generator | 30-Second Videos with Sound';
  const description =
    'Create videos with Kling 4.0: 3–30 second clips with stereo sound, up to 10 keyframes, Omni Reference with images and video clips, plus AI video editing and extension in 720p or 1080p.';

  const modes = [
    {
      tag: 'T2V',
      title: t('Text to Video'),
      description: t(
        'Describe the scene, the camera and the sound, and get a 3–30 second take in 720p or 1080p.',
      ),
      points: [t('16:9, 9:16, 1:1 and 21:9'), t('Stereo audio in every clip')],
      href: GENERATOR_URL,
    },
    {
      tag: 'I2V',
      title: t('Image to Video'),
      description: t(
        'Animate a photo and, if you like, lock the closing shot with an end image.',
      ),
      points: [t('Framing follows your image'), t('Optional last frame')],
      href: GENERATOR_URL,
    },
    {
      tag: 'KEYFRAMES',
      title: t('Keyframes'),
      description: t(
        'Line up to 10 images and Kling 4.0 connects them into one continuous take.',
      ),
      points: [
        t('First, in-between and last frames'),
        t('Played in the order you add them'),
      ],
      href: GENERATOR_URL,
    },
    {
      tag: 'REFERENCE',
      title: t('Omni Reference'),
      description: t(
        'Combine images and video clips to control the characters, the look and the motion.',
      ),
      points: [t('10 images · 5 clips'), t('@Image1-style prompt tags')],
      href: GENERATOR_URL,
    },
    {
      tag: 'EDIT',
      title: t('Video Edit'),
      description: t(
        'Upload a 3–30 second clip and describe the change. The scene, the motion and the original sound stay.',
      ),
      points: [t('Up to 4 reference images'), t('720p or 1080p')],
      href: EDIT_URL,
    },
    {
      tag: 'EXTEND',
      title: t('Video Extend'),
      description: t(
        'Add 3–30 seconds to the end of a clip. The new part continues from the last frame and is joined to the original.',
      ),
      points: [
        t('Your original stays intact'),
        t('Only the new seconds are billed'),
      ],
      href: EXTEND_URL,
    },
  ];

  const specs = [
    {
      label: t('Video length'),
      value: '3–30s',
      detail: t('Per generation or extension'),
    },
    {
      label: t('Resolution'),
      value: '1080p',
      detail: t('Or 720p at two-thirds of the price'),
    },
    { label: t('Formats'), value: '21:9', detail: '16:9 · 9:16 · 1:1' },
    {
      label: t('Audio'),
      value: t('Stereo'),
      detail: t('Generated with every clip'),
    },
    {
      label: t('Keyframes'),
      value: '10',
      detail: t('First, last and 8 in between'),
    },
    {
      label: t('Reference images'),
      value: '10',
      detail: t('Cited as @Image1, @Image2…'),
    },
    {
      label: t('Reference clips'),
      value: '5',
      detail: t('30 seconds combined'),
    },
    {
      label: t('Watermark'),
      value: t('None'),
      detail: t('Clean, logo-free downloads'),
    },
    {
      label: t('Edit source'),
      value: '3–30s',
      detail: t('Keeps the original sound'),
    },
    {
      label: t('Languages'),
      value: '9+',
      detail: t('Spoken dialogue and on-screen text'),
    },
  ];

  const tips = [
    {
      title: t('Direct the camera and the cuts'),
      description: t(
        'Say how the shot moves and when it changes. Kling 4.0 handles tracking, orbiting and handheld moves, and several shots in one take.',
      ),
      example: t(
        'Low tracking shot behind a sports car drifting across white dunes, sand spraying toward the lens. Cut to a wide aerial as the car straightens out.',
      ),
    },
    {
      title: t('Write the sound into the scene'),
      description: t(
        'Every clip has stereo audio, so put dialogue in quotes and describe the music and the ambience.',
      ),
      example: t(
        'In a recording booth, a young singer performs a soft ballad into the microphone. Close vocals, light room echo, a faint click of headphones.',
      ),
    },
    {
      title: t('Give every reference a job'),
      description: t(
        'When you attach images or clips, name each one and say what it controls.',
      ),
      example: t(
        '@Image1 is the dancer. Copy the camera move from @Video1. She says: “Let’s begin.”',
      ),
    },
    {
      title: t('Order keyframes like a storyboard'),
      description: t(
        'Add the frames in the order they should appear and describe what changes between them.',
      ),
      example: t(
        'Open on the empty stage, the dancer enters by the second frame, and the lights go down by the last one.',
      ),
    },
  ];

  const faqs = [
    {
      question: t('What is Kling 4.0?'),
      answer: t(
        'Kling 4.0 is the newest video model from Kling AI, Kuaishou’s video platform, released in October 2026. It generates 3–30 second clips with stereo sound from text, images, keyframes and multimodal references, and it can edit and extend existing videos.',
      ),
    },
    {
      question: t('How long can a Kling 4.0 video be?'),
      answer: t(
        'Each generation is 3 to 30 seconds long. The Extend Video tool adds another 3 to 30 seconds to a clip, and you can extend the result again.',
      ),
    },
    {
      question: t('Which resolutions and formats can I use?'),
      answer: t(
        'Easy-Peasy.AI renders Kling 4.0 in 720p or 1080p, in 16:9, 9:16, 1:1 or 21:9. Image-to-video and keyframe videos follow the shape of your first image.',
      ),
    },
    {
      question: t('Does Easy-Peasy.AI offer Kling 4.0 in 4K or HDR?'),
      answer: t(
        'Not yet. Kling has announced 4K and 10-bit HDR output for its own platform; on Easy-Peasy.AI, Kling 4.0 renders in 720p and 1080p.',
      ),
    },
    {
      question: t('Does Kling 4.0 generate sound?'),
      answer: t(
        'Yes. Every clip comes with synchronized stereo audio — dialogue, music, effects and ambience — so describe the sound in your prompt. Edits keep the original clip’s sound.',
      ),
    },
    {
      question: t('What can I use as references?'),
      answer: t(
        'Up to 10 images and 5 video clips (30 seconds combined). Refer to them in your prompt as @Image1 and @Video1, in the order you added them.',
      ),
    },
    {
      question: t('How do keyframes work?'),
      answer: t(
        'Add up to 10 images and choose Kling 4.0 Keyframes. The first image opens the clip, the last one closes it, and the others appear in between in the order you added them.',
      ),
    },
    {
      question: t('How much does Kling 4.0 cost on Easy-Peasy.AI?'),
      answer: t(
        'Generating and extending cost 8 credits per second at 720p and 12 at 1080p. Editing costs 10 credits per second of your clip at 720p and 15 at 1080p. Attaching reference video clips adds to the price. The exact total is shown before you start.',
      ),
    },
    {
      question: t('Can I use Kling 4.0 on the Free plan?'),
      answer: t(
        'Kling 4.0 is available on paid plans. On the Free plan you can try other models in the Video Generator.',
      ),
    },
    {
      question: t('Can I use Kling 4.0 videos commercially?'),
      answer: t(
        'Yes, on paid plans, subject to the Easy-Peasy.AI Terms of Use, Kling’s usage policies and your rights to any images or clips you upload.',
      ),
    },
    {
      question: t('Do the videos have a watermark?'),
      answer: t(
        'No. Videos you create with Kling 4.0 on Easy-Peasy.AI come without a visible watermark.',
      ),
    },
    {
      question: t('Where do the examples on this page come from?'),
      answer: t(
        'They are Kling AI’s own Kling 4.0 demonstrations, published in the official Kling 4.0 release note.',
      ),
    },
  ];

  const prefix = locale === 'en' ? '' : `/${locale}`;
  const canonical = `https://easy-peasy.ai${prefix}/ai-video-generator/kling-4-0`;
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#page`,
        url: canonical,
        name: t(title),
        description: t(description),
        inLanguage: locale,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Easy-Peasy.AI',
            item: `https://easy-peasy.ai${prefix}`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'AI Video Generator',
            item: `https://easy-peasy.ai${prefix}/ai-video-generator`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Kling 4.0',
            item: canonical,
          },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };

  return (
    <main
      className='relative overflow-hidden bg-[#050807] text-white'
      lang={locale}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <Seo
        templateTitle={t(title)}
        description={t(description)}
        image={poster('hero-spear-throw')}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
          }}
        />
      </Head>

      {/* Hero */}
      <section className='relative min-h-[760px] overflow-hidden sm:min-h-[96vh]'>
        <video
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          poster={poster('hero-spear-throw')}
          src={clip('hero-spear-throw')}
          className='absolute inset-0 h-full w-full object-cover'
        />
        <div className='absolute inset-0 bg-[#050807]/55' />
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,8,7,0.55)_60%,#050807_100%)]' />
        <div className='absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent to-[#050807]' />

        <div className='absolute start-0 top-0 z-30 w-full'>
          <LandingPageNavigation variant='light' forceDark />
        </div>

        <div className='relative z-20 mx-auto flex min-h-[760px] max-w-7xl items-center justify-center px-4 pb-20 pt-36 text-center sm:min-h-[96vh] sm:px-6 lg:px-8'>
          <div className='max-w-5xl'>
            <div className='mb-8 inline-flex max-w-full items-center gap-2.5 rounded-full border border-white/15 bg-black/30 py-1.5 pe-3.5 ps-1.5 text-[11px] font-medium text-slate-200 backdrop-blur-md sm:gap-3 sm:pe-4 sm:text-xs'>
              <span className='flex h-7 w-7 items-center justify-center rounded-full bg-white'>
                <KlingSvg className='h-4 w-4' aria-hidden='true' />
              </span>
              {t('Kuaishou’s newest video model, now on Easy-Peasy.AI')}
            </div>

            <h1 className='text-6xl font-semibold leading-[0.9] tracking-[-0.06em] text-white sm:text-8xl lg:text-[8.5rem]'>
              Kling{' '}
              <span className='bg-gradient-to-r from-teal-200 via-emerald-200 to-lime-200 bg-clip-text text-transparent'>
                4.0
              </span>
            </h1>

            <p className='mx-auto mt-8 max-w-3xl text-xl font-medium leading-relaxed text-slate-100 sm:text-2xl'>
              {t('Cinematic takes up to 30 seconds, with stereo sound.')}
            </p>
            <p className='mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg'>
              {t(
                'Start from text, a photo or up to 10 keyframes, guide it with reference images and clips, then edit or extend the result — in 720p or 1080p, from 9:16 to 21:9.',
              )}
            </p>

            <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <Link
                href={GENERATOR_URL}
                className='group inline-flex items-center justify-center rounded-full bg-gradient-to-r from-teal-200 to-lime-200 px-8 py-4 text-base font-semibold text-[#04140f] shadow-[0_20px_70px_-25px_rgba(94,234,212,0.9)] transition-transform hover:scale-[1.025]'
              >
                {t('Create with Kling 4.0')}
                <span className='ms-2 transition-transform group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1'>
                  →
                </span>
              </Link>
              <a
                href='#reel'
                className='px-4 py-3 text-sm font-semibold text-slate-200 transition-colors hover:text-teal-100'
              >
                {t('Watch the official examples')} ↓
              </a>
            </div>

            <div className='mt-8 flex justify-center'>
              <HappyUsers fromLandingPage={true} className='items-center' />
            </div>

            <dl className='mx-auto mt-14 grid max-w-3xl grid-cols-2 overflow-hidden rounded-2xl border border-white/10 bg-black/25 backdrop-blur-md sm:grid-cols-4'>
              {[
                ['3–30s', t('Single-pass clips')],
                ['1080p', t('Full HD output')],
                ['21:9', t('Ultrawide framing')],
                [t('Stereo'), t('Sound in every clip')],
              ].map(([value, label], index) => (
                <div
                  key={label}
                  className={clsxm(
                    'px-3 py-5',
                    index % 2 === 1 && 'border-s border-white/10',
                    index >= 2 && 'border-t border-white/10 sm:border-t-0',
                    index === 2 && 'sm:border-s',
                  )}
                >
                  <dt className='text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400'>
                    {label}
                  </dt>
                  <dd className='mt-1 text-2xl font-semibold text-teal-100'>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <LogosCloud />

      <Reel t={t} />

      <FramingSection t={t} />

      {/* Omni Reference */}
      <section
        id='reference'
        className='border-t border-white/[0.06] py-24 sm:py-32'
      >
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end'>
            <SectionHeading
              center={false}
              eyebrow={t('Omni Reference')}
              title={t('Show It What You Mean')}
              lead={t(
                'Give Kling 4.0 up to 10 images and 5 video clips in a single request. Name each one in your prompt — @Image1, @Video1 — and say what it is for.',
              )}
            />
            <ul className='grid gap-3 text-sm text-slate-300 sm:grid-cols-2 lg:grid-cols-1'>
              {[
                t('Up to 10 reference images'),
                t('Up to 5 clips, 30 seconds combined'),
                t('Clips guide the motion, camera and pacing'),
                t('Cited in the prompt by tag'),
              ].map((point) => (
                <li key={point} className='flex items-start gap-2.5'>
                  <Check />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className='mt-14 grid gap-8 md:grid-cols-3'>
            {REFERENCE_PAIRS.map((pair) => (
              <ReferenceCompare key={pair.result} pair={pair} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Keyframes */}
      <section
        id='keyframes'
        className='relative border-t border-white/[0.06] py-24 sm:py-32'
      >
        <div className='pointer-events-none absolute start-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-400/[0.07] blur-[120px] rtl:translate-x-1/2' />
        <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('Up to 10 Keyframes')}
            title={t('Direct the Story Frame by Frame')}
            lead={t(
              'Set the opening image, the closing image and up to eight moments in between. Kling 4.0 generates the motion that connects them, in the order you add them.',
            )}
          />
          <div className='mx-auto mt-16 max-w-5xl'>
            <div className='relative flex items-center justify-between'>
              <div className='absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-teal-300 via-emerald-300 to-lime-300 rtl:bg-gradient-to-l' />
              {Array.from({ length: 10 }, (_, index) => {
                const edge = index === 0 || index === 9;
                return (
                  <span
                    key={index}
                    className={clsxm(
                      'relative flex items-center justify-center rounded-full border font-semibold tabular-nums',
                      edge
                        ? 'h-12 w-12 border-teal-200 bg-[#071512] text-sm text-teal-100 sm:h-14 sm:w-14'
                        : 'h-7 w-7 border-white/20 bg-[#0b1110] text-[10px] text-slate-400 sm:h-9 sm:w-9 sm:text-xs',
                    )}
                  >
                    {index + 1}
                  </span>
                );
              })}
            </div>
            <div className='mt-5 flex justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500'>
              <span>{t('First frame')}</span>
              <span className='hidden sm:inline'>
                {t('Up to 8 in-between frames')}
              </span>
              <span>{t('Last frame')}</span>
            </div>
          </div>
          <p className='mx-auto mt-10 max-w-2xl text-center text-sm leading-relaxed text-slate-400'>
            {t(
              'In the Video Generator, attach your images and pick Kling 4.0 Keyframes. Two images make a first-and-last-frame transition; more turn into a storyboard.',
            )}
          </p>
        </div>
      </section>

      {/* Styles */}
      <section
        id='styles'
        className='border-t border-white/[0.06] py-24 sm:py-32'
      >
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('Any Look')}
            title={t('From Claymation to Cyberpunk')}
            lead={t(
              'The same model handles photoreal drama and stylized worlds. Hover over a style — or tap it — to play.',
            )}
          />
          <div className='mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
            {STYLES.map((item) => (
              <StyleTile key={item.clip} item={item} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Modes */}
      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('Six Ways to Create')}
            title={t('Every Kling 4.0 Mode in One Place')}
            lead={t(
              'Generate, direct with references and keyframes, then keep working on the clip — without leaving Easy-Peasy.AI.',
            )}
          />
          <div className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
            {modes.map((mode) => (
              <Link
                key={mode.tag}
                href={mode.href}
                className='group flex flex-col rounded-3xl border border-white/[0.08] bg-white/[0.02] p-7 transition-colors hover:border-teal-300/40 hover:bg-teal-300/[0.04]'
              >
                <span className='text-[10px] font-bold tracking-[0.22em] text-teal-200'>
                  {mode.tag}
                </span>
                <h3 className='mt-4 text-xl font-semibold text-white'>
                  {mode.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                  {mode.description}
                </p>
                <ul className='mt-6 space-y-2.5'>
                  {mode.points.map((point) => (
                    <li
                      key={point}
                      className='flex items-start gap-2.5 text-sm text-slate-300'
                    >
                      <Check />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <span className='mt-auto pt-7 text-sm font-semibold text-teal-200 transition-colors group-hover:text-white'>
                  {t('Open')}{' '}
                  <span
                    className='inline-block rtl:-scale-x-100'
                    aria-hidden='true'
                  >
                    →
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section
        id='pricing'
        className='border-t border-white/[0.06] py-24 sm:py-32'
      >
        <div className='mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:px-8'>
          <div>
            <SectionHeading
              center={false}
              eyebrow={t('Simple Pricing')}
              title={t('Know the Cost Before You Create')}
              lead={t(
                'Kling 4.0 is billed per second of video. Pick what you are making, how long it runs and the resolution.',
              )}
            />
            <ul className='mt-8 space-y-3 text-sm text-slate-300'>
              {[
                t('No base fee — you pay for the seconds you render'),
                t('Reference images are free to add'),
                t('Available on all paid plans'),
              ].map((point) => (
                <li key={point} className='flex items-start gap-2.5'>
                  <Check />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <CreditCalculator pricing={pricing} t={t} />
        </div>
      </section>

      {/* Specs */}
      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('Specifications')}
            title={t('Kling 4.0 at a Glance')}
          />
          <dl className='mx-auto mt-14 grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
            {specs.map((spec) => (
              <div
                key={spec.label}
                className='rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5'
              >
                <dt className='text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500'>
                  {spec.label}
                </dt>
                <dd className='mt-2 text-2xl font-semibold text-teal-100'>
                  {spec.value}
                </dd>
                <dd className='mt-1 text-xs leading-relaxed text-slate-500'>
                  {spec.detail}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Prompt guide */}
      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('Prompt Guide')}
            title={t('Write Prompts Kling 4.0 Can Follow')}
          />
          <div className='mt-14 grid gap-6 lg:grid-cols-2'>
            {tips.map((tip) => (
              <article
                key={tip.title}
                className='rounded-3xl border border-white/[0.08] bg-white/[0.02] p-7 sm:p-8'
              >
                <h3 className='text-lg font-semibold text-white'>
                  {tip.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                  {tip.description}
                </p>
                <div className='mt-5 rounded-2xl border border-white/[0.06] bg-black/30 p-4'>
                  <div className='mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                    {t('Example prompt')}
                  </div>
                  <p className='text-sm italic leading-relaxed text-slate-200'>
                    {tip.example}
                  </p>
                </div>
              </article>
            ))}
          </div>
          <div className='mt-14 flex justify-center'>
            <Link
              href={GENERATOR_URL}
              className='inline-flex items-center rounded-full bg-white px-8 py-4 text-base font-semibold text-[#06110f] transition-transform hover:scale-[1.02]'
            >
              {t('Create with Kling 4.0')}
            </Link>
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      {/* FAQ */}
      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <SectionHeading
            eyebrow={t('FAQ')}
            title={t('Frequently Asked Questions')}
            lead={t('Everything you need to know about Kling 4.0')}
          />
          <div className='mx-auto mt-14 max-w-3xl space-y-3'>
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className='group rounded-2xl border border-white/[0.08] bg-white/[0.02]'
              >
                <summary className='flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-start font-semibold text-white'>
                  {faq.question}
                  <span className='text-xl text-teal-200 transition-transform group-open:rotate-45'>
                    +
                  </span>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm leading-relaxed text-slate-400'>
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos' />
        <Footer />
      </div>
    </main>
  );
}
